<template>
  <div class="d-flex flex-column mt-4">
    <div class="grid-header">
      <h1>{{ getTitle()}} </h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearRelacionClienteInmobiliario()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card">
      <DxColumnFixing :enabled="true" />

      <DxColumn data-field="Documento" :caption="$t('clienteInmobiliario.n_documento')"
        :customize-text="customizeTextUppercase"></DxColumn>
      <DxColumn data-field="NombreCompleto" :caption="$t('clienteInmobiliario.nombre')"></DxColumn>
      <DxColumn data-field="Email" :caption="$t('clienteInmobiliario.email')"></DxColumn>
      <DxColumn data-field="TelefonoMovil" :caption="$t('clienteInmobiliario.telMovil')"></DxColumn>
      <DxColumn data-field="TelefonoFijo" :caption="$t('clienteInmobiliario.telFijo')"></DxColumn>
      <DxColumn data-field="FechaInicio" :caption="$t('clienteInmobiliario.fechaInicio')"></DxColumn> 
      <DxColumn data-field="FechaFin" :caption="$t('clienteInmobiliario.fechaFin')"></DxColumn>
      <DxColumn type="buttons" :caption="$t('inmuebles.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-edit text-success',
            hint: $t('inmuebles.grid.modificar'),
            onClick: (e: any) => verDetalle(e.row.data.Id, e.row.data.IdClienteInmobiliario)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-file-pen text-success',
            hint: $t('inmuebles.grid.enviar_autorizacion'),
            onClick: (e: any) => enviarAutorizacionTratamientoDatos(e.row.data.IdClienteInmobiliario),
            disabled: (options: any) => !options.row?.data.PuedeEnviarAutorizacion
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-upload text-success',
            hint: $t('clientes.grid.subir_firma_a_mano'),
            onClick: (e: any) => subirFirmaAMano(e.row.data.IdClienteInmobiliario, e.row.data.RutaAutorizacionFirmada),
            disabled: (options: any) => !options.row?.data.PuedeEnviarAutorizacion
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-envelope text-success',
            hint: $t('clientes.grid.enviar_recordatorio_firma'),
            onClick: (e: any) => enviarRecordatorioFirma(e.row.data.IdClienteInmobiliario),
            disabled: (options: any) => !options.row?.data.PuedeEnviarRecordatorioFirmaAutorizacion
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-xmark text-success',
            hint: $t('clientes.grid.cancelar_firma'),
            onClick: (e: any) => cancelarFirma(e.row.data.IdClienteInmobiliario),
            disabled: (options: any) => !options.row?.data.PuedeCancelarFirmaAutorizacion
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-plus text-success',
            hint: $t('clientes.grid.nueva_venta'),
            onClick: (e: any) => nuevaVenta(e.row.data.IdClienteInmobiliario),
            disabled: (options: any) => !options.row?.data.PuedeCrearBorrador
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-trash text-success',
            hint: $t('inmuebles.grid.borrar'),
            onClick: (e: any) => borrar(e.row.data.Id)
          }
        ]"/>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, type PropType } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing } from "devextreme-vue/data-grid";
import { customizeTextUppercase, getDefaultGridProperties } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import { useRouter } from "@/.shared/services/routing-services";
import { TipoPersonaEnum, TipoRelacionClienteInmobiliarioEnum, type DXLoadOptions } from "@api/api";
import RelacionClienteInmobiliarioForm from "./RelacionClienteInmobiliarioForm.vue";
import { messageBoxResult } from '@components/dialogs/message-box';
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import InputTextBox from '@/.shared/components/dialogs/InputTextBox.vue';
import { Constants } from "@/.shared/constants/ClienteInmobiliario";

interface Props {
  idInmueble: string;
  tipoRelacion: TipoRelacionClienteInmobiliarioEnum;
}

interface ModalResult {
  result: string;
  value: string | null;
}

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const router = useRouter();
const props = defineProps<Props>();
const emit = defineEmits(['nuevaVenta']);

onMounted(async () => {
  await setDataSource();
});

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getClientesInmuebleForGrid", { showLoading: false }, props.idInmueble, props.tipoRelacion, q)
    }
  });
}

const crearRelacionClienteInmobiliario = async () => {
  let title = props.tipoRelacion == TipoRelacionClienteInmobiliarioEnum.Inquilino
    ? `${intl.i18n.t("relacionClienteInmobiliario.ui.nuevo_inquilino")}`
    : `${intl.i18n.t("relacionClienteInmobiliario.ui.nuevo_propietario")}`;

  const value = await modal.showModal(
    RelacionClienteInmobiliarioForm,
    {
      tipoRelacion: props.tipoRelacion,
      idInmueble: props.idInmueble,
    },
    { title: title, width: 1100, height: 600 }
  );

  if (value == messageBoxResult.ok)
    grid.value?.instance?.refresh();
}

const verDetalle = async (idRelacionClienteInmobiliario: string, idClienteInmobiliario: string) => {
  let title = props.tipoRelacion == TipoRelacionClienteInmobiliarioEnum.Inquilino
    ? `${intl.i18n.t("relacionClienteInmobiliario.ui.edicion_inquilino")}`
    : `${intl.i18n.t("relacionClienteInmobiliario.ui.edicion_propietario")}`;
  await modal.showModal(
    RelacionClienteInmobiliarioForm,
    { 
      idInmueble: props.idInmueble, 
      tipoRelacion: props.tipoRelacion, 
      idRelacionClienteInmobiliario: idRelacionClienteInmobiliario,
      idClienteInmobiliario: idClienteInmobiliario 
    },
    { title: title, width: 1100, height: 600 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
};

const enviarAutorizacionTratamientoDatos = async (idClienteInmobiliario: string) => {
  const emailAlternativo = await modal.showModal(
    InputTextBox,
    {
      label: intl.i18n.t('relacionClienteInmobiliario.ui.email_alternativo'),
      required: false,
      isEmail: true
    },
    { 
      title: intl.i18n.t('relacionClienteInmobiliario.ui.titulo_email_alternativo'),
      width: 600,
      height: 280
    }
  ) as ModalResult;

  if (emailAlternativo?.result === messageBoxResult.ok) {
    await api.callStandardApi("iniciarFirmaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario, { Email: emailAlternativo.value })
      .then(() => {
        toastSuccess(intl.i18n.t('relacionClienteInmobiliario.ui.autorizacion_enviada'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('relacionClienteInmobiliario.ui.autorizacion_fallo')));
  }
}

const borrar = async (idRelacionClienteInmobiliario: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t('relacionClienteInmobiliario.ui.pregunta_borrar_relacion'), intl.i18n.t('relacionClienteInmobiliario.ui.titulo_borrar_relacion'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteRelacionClienteInmobiliarioInmueble", { showLoading: true }, props.idInmueble, idRelacionClienteInmobiliario)
      .then(() => {
        toastSuccess(intl.i18n.t('relacionClienteInmobiliario.ui.borrado_exito'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('relacionClienteInmobiliario.ui.borrado_fallo')));

};

const getTitle = () => {
  return props.tipoRelacion == TipoRelacionClienteInmobiliarioEnum.Inquilino
    ? `${intl.i18n.t("relacionClienteInmobiliario.ui.titulo_inquilinos")}`
    : `${intl.i18n.t("relacionClienteInmobiliario.ui.titulo_propietarios")}`;
}

const nuevaVenta = async (idClienteInmobiliario: string) => {
  await router.push(intl.i18Route({ name: "nuevo-borrador-operacion", query: { idInmueble: props.idInmueble, idClienteInmobiliario: idClienteInmobiliario } }));
  emit('nuevaVenta');
}

const subirFirmaAMano = async (idClienteInmobiliario: string, ruta: string | undefined) => {

  const detailCliente = await api.callStandardApi("getClienteInmobiliarioById", { showLoading: true }, idClienteInmobiliario);
  var tipoPersona = detailCliente.TipoPersona;

  var documentUriTemplate = await api.callStandardApi("getPlantillaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario);

  const autorizacionPropia = await modal.uploadFileBox({ title: intl.i18n.t('clientes.grid.autorizacion_propia'), required: true,
  documentUri: ruta, templateUri: documentUriTemplate},  intl.i18n.t('clientes.grid.subir_firma_a_mano'));

  if (!!autorizacionPropia) {
    try {
      await api.callStandardApi("subirAutorizacionClienteInmobiliario", { showLoading: false }, idClienteInmobiliario, autorizacionPropia.name, autorizacionPropia);

      toastSuccess(intl.i18n.t('clientes.grid.autorizacion_subida'));
      grid.value?.instance!.refresh();
    }
    catch (e: any) {
      toastError(intl.i18n.t('clientes.grid.autorizacion_subida_fallo'));
    }
  }
}

const enviarRecordatorioFirma = async (idClienteInmobiliario: string) => {
  try {
    await api.callStandardApi("enviarRecordatorioFirmaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario);
    toastSuccess(intl.i18n.t('clientes.grid.recordatorio_enviado'));
    grid.value?.instance?.refresh();
  } catch (error) {
    toastError(intl.i18n.t('clientes.grid.recordatorio_fallo'));
  }
}

const cancelarFirma = async (idClienteInmobiliario: string) => {
  try {
    await api.callStandardApi("cancelarFirmaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario);
    toastSuccess(intl.i18n.t('clientes.grid.firma_cancelada'));
    grid.value?.instance?.refresh();
  } catch (error) {
    toastError(intl.i18n.t('clientes.grid.firma_cancelada_fallo'));
  }
}

</script>

<style lang="scss" scoped>
</style>